class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent, searchParams)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent, searchParams);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static settleSupersededUpdate(updateEvent) {
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    updateEvent?.resolve(currentCount);
  }

  static renderSectionFromFetch(url, event, updateEvent, requestSearchParams) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        if (requestSearchParams !== FacetFiltersForm.searchParamsPrev) {
          FacetFiltersForm.settleSupersededUpdate(updateEvent);
          return;
        }
        FacetFiltersForm.renderSection(html, event, updateEvent, requestSearchParams);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent, requestSearchParams) {
    if (requestSearchParams !== FacetFiltersForm.searchParamsPrev) {
      FacetFiltersForm.settleSupersededUpdate(updateEvent);
      return;
    }
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent, requestSearchParams);
  }

  static renderSection(html, event, updateEvent, requestSearchParams) {
    FacetFiltersForm.renderFilters(html, event, requestSearchParams);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (!sourceCount || !container) {
      document
        .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
        .forEach((spinner) => spinner.classList.add('hidden'));
      updateEvent?.resolve(0);
      return;
    }
    const count = sourceCount.innerHTML;
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static filterPairKey(name, value) {
    return `${String(name)}\u0000${String(value)}`;
  }

  static isFilterParam(name) {
    return String(name).startsWith('filter.');
  }

  static isPriceRangeFilterParam(name) {
    return /^filter\.[^.]+\.price\.(?:gte|lte)$/.test(String(name));
  }

  static isHiddenRegionFilterValue(name, value) {
    if (name !== 'filter.p.m.custom.omr_de') return false;

    const valueHandle = String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return valueHandle === 'vin-de-france' || valueHandle === 'vendee-france';
  }

  static targetFilterParams(searchParams = FacetFiltersForm.searchParamsPrev) {
    return new URLSearchParams(searchParams);
  }

  static filterControlPair(control) {
    if (!control?.name || !FacetFiltersForm.isFilterParam(control.name)) return '';
    return FacetFiltersForm.filterPairKey(control.name, control.value);
  }

  static isDiscreteFacetControl(control) {
    return ['checkbox', 'radio'].includes(String(control?.type).toLowerCase());
  }

  static hasSelectedTargetControl(element, targetParams) {
    if (!element) return false;
    const representedNames = new Set(
      Array.from(element.querySelectorAll('input[name^="filter."]'))
        .filter((input) => FacetFiltersForm.isDiscreteFacetControl(input))
        .map((input) => input.name),
    );
    if (element.dataset?.facetParam) representedNames.add(element.dataset.facetParam);

    return Array.from(targetParams).some(([name, value]) => {
      return representedNames.has(name) && !FacetFiltersForm.isHiddenRegionFilterValue(name, value);
    });
  }

  static facetOptionTemplateRegistry() {
    if (!FacetFiltersForm.facetOptionTemplates?.get || !FacetFiltersForm.facetOptionTemplates?.set) {
      FacetFiltersForm.facetOptionTemplates = new Map();
    }
    return FacetFiltersForm.facetOptionTemplates;
  }

  static activeFacetChipTemplateRegistry() {
    if (!FacetFiltersForm.activeFacetChipTemplates?.get || !FacetFiltersForm.activeFacetChipTemplates?.set) {
      FacetFiltersForm.activeFacetChipTemplates = new Map();
    }
    return FacetFiltersForm.activeFacetChipTemplates;
  }

  static facetLabelRegistry() {
    if (!FacetFiltersForm.facetLabels?.get || !FacetFiltersForm.facetLabels?.set) {
      FacetFiltersForm.facetLabels = new Map();
    }
    return FacetFiltersForm.facetLabels;
  }

  static facetItemForInput(input) {
    return input?.closest?.('.facets__item, .mobile-facets__item, li') || null;
  }

  static facetGroupScope(element, input, item = FacetFiltersForm.facetItemForInput(input)) {
    if (element?.id) return element.id;
    const variant = item?.classList?.contains('mobile-facets__item') ? 'mobile' : 'desktop';
    return `${variant}:${input?.name || element?.dataset?.facetParam || ''}`;
  }

  static facetOptionTemplateKey(element, input, item) {
    const pairKey = FacetFiltersForm.filterControlPair(input);
    if (!pairKey) return '';
    return `${FacetFiltersForm.facetGroupScope(element, input, item)}\u0000${pairKey}`;
  }

  static rememberFacetOptionTemplates(elements) {
    const registry = FacetFiltersForm.facetOptionTemplateRegistry();
    const labels = FacetFiltersForm.facetLabelRegistry();

    Array.from(elements || []).forEach((element) => {
      Array.from(element?.querySelectorAll?.('input[name^="filter."]') || [])
        .filter((input) => FacetFiltersForm.isDiscreteFacetControl(input))
        .forEach((input) => {
          if (FacetFiltersForm.isHiddenRegionFilterValue(input.name, input.value)) return;
          const item = FacetFiltersForm.facetItemForInput(input);
          const templateKey = FacetFiltersForm.facetOptionTemplateKey(element, input, item);
          if (item && templateKey) registry.set(templateKey, item.cloneNode(true));
          if (element.dataset?.facetLabel) labels.set(input.name, element.dataset.facetLabel);
        });
    });
  }

  static nextRestoredInputId(name) {
    FacetFiltersForm.restoredInputId = (FacetFiltersForm.restoredInputId || 0) + 1;
    const safeName = String(name || 'filter')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-');
    return `Facet-restored-${safeName}-${FacetFiltersForm.restoredInputId}`;
  }

  static setSelectedFacetValueState(item, input, regenerateId = false) {
    if (!input) return;

    input.checked = true;
    input.defaultChecked = true;
    input.setAttribute?.('checked', '');
    input.disabled = false;
    input.removeAttribute?.('disabled');

    if (regenerateId) {
      const inputId = FacetFiltersForm.nextRestoredInputId(input.name);
      input.id = inputId;
      input.setAttribute?.('id', inputId);
      const label = item?.querySelector?.('label');
      if (label) {
        label.htmlFor = inputId;
        label.setAttribute?.('for', inputId);
      }
    }

    item?.classList?.remove('hidden');
    const label = item?.querySelector?.('.facets__label, .mobile-facets__label');
    label?.classList?.remove('disabled');
    label?.classList?.add('active');
  }

  static setUnselectedFacetValueState(item, input) {
    if (!input) return;
    input.checked = false;
    input.defaultChecked = false;
    input.removeAttribute?.('checked');
    item?.querySelector?.('.facets__label, .mobile-facets__label')?.classList?.remove('active');
  }

  static reconcileFacetControlSelection(element, targetParams) {
    Array.from(element?.querySelectorAll?.('input[name^="filter."]') || [])
      .filter((input) => FacetFiltersForm.isDiscreteFacetControl(input))
      .forEach((input) => {
        const selected =
          !FacetFiltersForm.isHiddenRegionFilterValue(input.name, input.value) &&
          targetParams.getAll(input.name).includes(String(input.value));
        const item = FacetFiltersForm.facetItemForInput(input);
        if (selected) FacetFiltersForm.setSelectedFacetValueState(item, input);
        else FacetFiltersForm.setUnselectedFacetValueState(item, input);
      });
  }

  static rehydrateSelectedFacetValues(sourceElement, currentElement, targetParams) {
    if (!sourceElement) return;

    FacetFiltersForm.rememberFacetOptionTemplates([sourceElement, currentElement].filter(Boolean));
    const registry = FacetFiltersForm.facetOptionTemplateRegistry();
    const sourceInputs = Array.from(sourceElement.querySelectorAll('input[name^="filter."]')).filter((input) =>
      FacetFiltersForm.isDiscreteFacetControl(input),
    );
    const currentInputs = Array.from(currentElement?.querySelectorAll?.('input[name^="filter."]') || []).filter(
      (input) => FacetFiltersForm.isDiscreteFacetControl(input),
    );
    const sourcePairs = new Map(
      sourceInputs.map((input) => [FacetFiltersForm.filterControlPair(input), input]).filter(([pairKey]) => pairKey),
    );
    const representedNames = new Set([...sourceInputs, ...currentInputs].map((input) => input.name));
    if (sourceElement.dataset?.facetParam) representedNames.add(sourceElement.dataset.facetParam);
    if (currentElement?.dataset?.facetParam) representedNames.add(currentElement.dataset.facetParam);

    targetParams.forEach((value, name) => {
      if (!representedNames.has(name) || FacetFiltersForm.isHiddenRegionFilterValue(name, value)) return;
      const pairKey = FacetFiltersForm.filterPairKey(name, value);
      const sourceInput = sourcePairs.get(pairKey);
      if (sourceInput) {
        FacetFiltersForm.setSelectedFacetValueState(FacetFiltersForm.facetItemForInput(sourceInput), sourceInput);
        return;
      }

      const currentInput = currentInputs.find((input) => FacetFiltersForm.filterControlPair(input) === pairKey);
      const currentItem = FacetFiltersForm.facetItemForInput(currentInput);
      const scopeItem = currentItem || FacetFiltersForm.facetItemForInput(sourceInputs.find((input) => input.name === name));
      const templateKey = FacetFiltersForm.facetOptionTemplateKey(sourceElement, { name, value }, scopeItem);
      const restoredItem = currentItem?.cloneNode(true) || registry.get(templateKey)?.cloneNode(true);
      if (!restoredItem) return;

      const restoredInput = Array.from(restoredItem.querySelectorAll('input[name^="filter."]')).find(
        (input) => FacetFiltersForm.filterControlPair(input) === pairKey,
      );
      if (!restoredInput) return;

      FacetFiltersForm.setSelectedFacetValueState(restoredItem, restoredInput, true);
      const sourceList = sourceElement.querySelector(
        restoredItem.classList.contains('mobile-facets__item')
          ? '.mobile-facets__list'
          : '.facets__list--vertical, .facets__list',
      );
      if (!sourceList) return;

      sourceList.append(restoredItem);
      sourcePairs.set(pairKey, restoredInput);
      registry.set(templateKey, restoredItem.cloneNode(true));
    });
  }

  static activeFacetChipKey(selector, name, value) {
    return `${selector}\u0000${FacetFiltersForm.filterPairKey(name, value)}`;
  }

  static rememberActiveFacetChipTemplates(root) {
    const registry = FacetFiltersForm.activeFacetChipTemplateRegistry();
    const labels = FacetFiltersForm.facetLabelRegistry();

    ['.active-facets-mobile', '.active-facets-desktop'].forEach((selector) => {
      const container = root?.querySelector?.(selector);
      if (!container) return;
      Array.from(container.querySelectorAll('facet-remove[data-facet-param][data-facet-value]')).forEach((chip) => {
        const { facetParam: name, facetValue: value, facetLabel: label } = chip.dataset;
        if (FacetFiltersForm.isHiddenRegionFilterValue(name, value)) return;
        registry.set(FacetFiltersForm.activeFacetChipKey(selector, name, value), chip.cloneNode(true));
        if (label) labels.set(name, label);
      });
    });
  }

  static filterRemovalUrl(searchParams, name, value) {
    const nextParams = new URLSearchParams(searchParams);
    const retainedValues = [];
    let removed = false;

    nextParams.getAll(name).forEach((currentValue) => {
      if (!removed && currentValue === String(value)) {
        removed = true;
      } else {
        retainedValues.push(currentValue);
      }
    });
    nextParams.delete(name);
    retainedValues.forEach((retainedValue) => nextParams.append(name, retainedValue));

    const query = nextParams.toString();
    return `${window.location.pathname}${query ? `?${query}` : ''}`;
  }

  static createActiveFacetChip(root, selector, name, value, targetParams) {
    const registry = FacetFiltersForm.activeFacetChipTemplateRegistry();
    const exactTemplate = registry.get(FacetFiltersForm.activeFacetChipKey(selector, name, value));
    const genericTemplate = Array.from(registry.entries()).find(([key, chip]) => {
      return key.startsWith(`${selector}\u0000`) && chip.dataset?.facetParam === name;
    })?.[1];
    const scaffold = root?.querySelector?.('template[data-active-facet-chip-template]');
    const chip =
      exactTemplate?.cloneNode(true) ||
      genericTemplate?.cloneNode(true) ||
      scaffold?.content?.firstElementChild?.cloneNode(true);
    if (!chip) return null;

    const facetLabel = FacetFiltersForm.facetLabelRegistry().get(name) || name;
    chip.dataset.facetParam = name;
    chip.dataset.facetValue = String(value);
    chip.dataset.facetLabel = facetLabel;
    chip.setAttribute?.('data-facet-param', name);
    chip.setAttribute?.('data-facet-value', String(value));
    chip.setAttribute?.('data-facet-label', facetLabel);
    const label = chip.querySelector?.('[data-facet-chip-label]');
    if (label) label.textContent = `${facetLabel}: ${value}`;
    const link = chip.querySelector?.('a');
    if (!link) return null;
    const removalUrl = FacetFiltersForm.filterRemovalUrl(targetParams, name, value);
    if (link.setAttribute) link.setAttribute('href', removalUrl);
    else link.href = removalUrl;

    registry.set(FacetFiltersForm.activeFacetChipKey(selector, name, value), chip.cloneNode(true));
    return chip;
  }

  static targetActiveFilterCount(targetParams) {
    const discretePairs = new Set();
    let hasPriceRange = false;

    targetParams.forEach((value, name) => {
      if (!FacetFiltersForm.isFilterParam(name) || FacetFiltersForm.isHiddenRegionFilterValue(name, value)) return;
      if (String(value).trim() === '') return;
      if (FacetFiltersForm.isPriceRangeFilterParam(name)) {
        hasPriceRange = true;
      } else {
        discretePairs.add(FacetFiltersForm.filterPairKey(name, value));
      }
    });
    return discretePairs.size + (hasPriceRange ? 1 : 0);
  }

  static ariaLabelWithCount(label, count) {
    if (!label) return String(count);
    return /\d+/.test(label) ? label.replace(/\d+/, String(count)) : `${label} (${count})`;
  }

  static reconcileActiveFacetCount(root, targetParams) {
    const count = FacetFiltersForm.targetActiveFilterCount(targetParams);
    let badges = Array.from(root?.querySelectorAll?.('.mobile-facets__active-count') || []);
    if (count > 0 && badges.length === 0) {
      const badgeScaffold = root?.querySelector?.('template[data-mobile-active-count-template]');
      const mobileOpen = root?.querySelector?.('.mobile-facets__open');
      const badge = badgeScaffold?.content?.firstElementChild?.cloneNode(true);
      if (badge && mobileOpen) {
        mobileOpen.append(badge);
        badges = [badge];
      }
    }
    badges.forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = count === 0;
      const ariaLabel = badge.getAttribute?.('aria-label');
      badge.setAttribute?.('aria-label', FacetFiltersForm.ariaLabelWithCount(ariaLabel, count));
    });
    Array.from(root?.querySelectorAll?.('[data-active-facets-scroll]') || []).forEach((rail) => {
      const ariaLabel = rail.getAttribute?.('aria-label');
      rail.setAttribute?.('aria-label', FacetFiltersForm.ariaLabelWithCount(ariaLabel, count));
      rail.setAttribute?.('data-active-count', String(count));
    });
    return count;
  }

  static renderFilters(html, event, requestSearchParams = FacetFiltersForm.searchParamsPrev) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const targetParams = FacetFiltersForm.targetFilterParams(requestSearchParams);
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    FacetFiltersForm.rememberFacetOptionTemplates(facetDetailsElementsFromDom);
    FacetFiltersForm.rememberFacetOptionTemplates(facetDetailsElementsFromFetch);
    FacetFiltersForm.rememberActiveFacetChipTemplates(document);
    FacetFiltersForm.rememberActiveFacetChipTemplates(parsedHTML);
    const originatingFilter = event ? event.target.closest('.js-filter') : null;
    const originatingFilterId = originatingFilter?.id;
    const originatingDrawer = originatingFilter?.closest('menu-drawer');
    const focusFallbackIds = [originatingFilter?.nextElementSibling?.id, originatingFilter?.previousElementSibling?.id]
      .filter(Boolean);
    const sourceOrderByParent = new Map();

    facetDetailsElementsFromFetch.forEach((element) => {
      const parentId = element.parentElement?.id;
      if (!parentId || sourceOrderByParent.has(parentId)) return;
      sourceOrderByParent.set(
        parentId,
        Array.from(element.parentElement.children)
          .filter((child) => child.classList.contains('js-filter'))
          .map((child) => child.id),
      );
    });

    // Retain a selected facet group if Shopify prunes it, but make the target URL its exact state.
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) return;
      if (!FacetFiltersForm.hasSelectedTargetControl(currentElement, targetParams)) {
        currentElement.remove();
        return;
      }
      FacetFiltersForm.reconcileFacetControlSelection(currentElement, targetParams);
      FacetFiltersForm.rehydrateSelectedFacetValues(currentElement, currentElement, targetParams);
    });

    const matchesId = (element) => {
      return originatingFilterId ? element.id === originatingFilterId : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        FacetFiltersForm.rehydrateSelectedFacetValues(elementToRender, currentElement, targetParams);
        currentElement.innerHTML = elementToRender.innerHTML;
      } else {
        FacetFiltersForm.rehydrateSelectedFacetValues(elementToRender, null, targetParams);
        const sourceParent = elementToRender.parentElement;
        const targetParent = sourceParent?.id ? document.getElementById(sourceParent.id) : null;
        if (!targetParent) return;

        const sourceSiblingIds = sourceOrderByParent.get(sourceParent.id) || [];
        const sourceIndex = sourceSiblingIds.indexOf(elementToRender.id);
        const previousExisting = sourceSiblingIds
          .slice(0, sourceIndex)
          .reverse()
          .map((siblingId) => document.getElementById(siblingId))
          .find(Boolean);
        const nextExisting = sourceSiblingIds
          .slice(sourceIndex + 1)
          .map((siblingId) => document.getElementById(siblingId))
          .find(Boolean);

        if (previousExisting) previousExisting.after(elementToRender);
        else if (nextExisting) nextExisting.before(elementToRender);
        else targetParent.append(elementToRender);
      }
    });

    if (countsToRender && originatingFilter) {
      FacetFiltersForm.rehydrateSelectedFacetValues(countsToRender, originatingFilter, targetParams);
    }

    FacetFiltersForm.renderActiveFacets(parsedHTML, targetParams);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (originatingFilterId && !document.getElementById(originatingFilterId)) {
      const siblingFallback = focusFallbackIds
        .map((id) => document.getElementById(id))
        .find(Boolean)
        ?.querySelector('summary.mobile-facets__summary, .facets__summary');
      const drawerFallback =
        originatingDrawer?.querySelector('[data-mobile-facets-close]') ||
        originatingDrawer?.querySelector('details[data-mobile-facet-accordion] > summary') ||
        originatingDrawer?.querySelector('.mobile-facets__open-wrapper') ||
        document.querySelector('.mobile-facets__open-wrapper');
      (siblingFallback || drawerFallback)?.focus();
    }

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? newFacetDetailsElement.hasAttribute('data-mobile-facet-accordion')
                ? `.mobile-facets__summary`
                : `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html, targetParams = FacetFiltersForm.targetFilterParams()) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const activeFacetRail = activeFacetsElement.querySelector('active-facets-scroll') || activeFacetsElement;
      const sourcePairs = new Set();
      Array.from(
        activeFacetsElement.querySelectorAll('facet-remove[data-facet-param][data-facet-value]'),
      ).forEach((chip) => {
        const { facetParam: name, facetValue: value } = chip.dataset;
        const pairKey = FacetFiltersForm.filterPairKey(name, value);
        if (!targetParams.getAll(name).includes(String(value))) {
          chip.remove();
          return;
        }
        chip
          .querySelector('a')
          ?.setAttribute('href', FacetFiltersForm.filterRemovalUrl(targetParams, name, value));
        sourcePairs.add(pairKey);
      });

      targetParams.forEach((value, name) => {
        if (
          !FacetFiltersForm.isFilterParam(name) ||
          FacetFiltersForm.isPriceRangeFilterParam(name) ||
          FacetFiltersForm.isHiddenRegionFilterValue(name, value) ||
          String(value).trim() === ''
        )
          return;
        const pairKey = FacetFiltersForm.filterPairKey(name, value);
        if (sourcePairs.has(pairKey)) return;

        const restoredChip = FacetFiltersForm.createActiveFacetChip(html, selector, name, value, targetParams);
        if (!restoredChip) return;
        activeFacetRail.append(restoredChip);
        sourcePairs.add(pairKey);
      });

    });

    FacetFiltersForm.reconcileActiveFacetCount(html, targetParams);
    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const currentActiveFacetsElement = document.querySelector(selector);
      if (activeFacetsElement && currentActiveFacetsElement) {
        currentActiveFacetsElement.innerHTML = activeFacetsElement.innerHTML;
      }
    });
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);
    const controls = Array.from(form.elements || form.querySelectorAll('[name]'));
    const filterControls = controls.filter((control) => FacetFiltersForm.isFilterParam(control.name));
    if (filterControls.length === 0) return searchParams.toString();

    const representedPairs = new Set(
      filterControls.map((control) => FacetFiltersForm.filterPairKey(control.name, control.value)),
    );
    const representedScalarNames = new Set(
      filterControls
        .filter((control) => !['checkbox', 'radio'].includes(String(control.type).toLowerCase()))
        .map((control) => control.name),
    );
    const submittedPairs = new Set();
    searchParams.forEach((value, name) => {
      if (FacetFiltersForm.isFilterParam(name)) {
        submittedPairs.add(FacetFiltersForm.filterPairKey(name, value));
      }
    });

    FacetFiltersForm.targetFilterParams().forEach((value, name) => {
      if (!FacetFiltersForm.isFilterParam(name) || FacetFiltersForm.isHiddenRegionFilterValue(name, value)) return;
      const pairKey = FacetFiltersForm.filterPairKey(name, value);
      if (!representedScalarNames.has(name) && !representedPairs.has(pairKey) && !submittedPairs.has(pairKey)) {
        searchParams.append(name, value);
        submittedPairs.add(pairKey);
      }
    });

    return searchParams.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class ActiveFacetsScroll extends HTMLElement {
  constructor() {
    super();
    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleMotionPreference = this.handleMotionPreference.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleUserTakeover = this.handleUserTakeover.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.runHintFrame = this.runHintFrame.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.userHasTakenOver = false;
    this.hasHinted = false;
    this.isIntersecting = typeof IntersectionObserver === 'undefined';
    this.reducedMotionQuery =
      typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

    this.addEventListener('pointerdown', this.handleUserTakeover, { passive: true });
    this.addEventListener('touchstart', this.handleUserTakeover, { passive: true });
    this.addEventListener('wheel', this.handleUserTakeover, { passive: true });
    this.addEventListener('keydown', this.handleUserTakeover);
    this.addEventListener('focusin', this.handleUserTakeover);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.reducedMotionQuery?.addEventListener?.('change', this.handleMotionPreference);

    if (typeof IntersectionObserver !== 'undefined') {
      this.intersectionObserver = new IntersectionObserver(this.handleIntersection);
      this.intersectionObserver.observe(this);
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this);
    }

    this.setupFrame = requestAnimationFrame(() => {
      this.setupFrame = null;
      if (this.isConnected && this.initialized) this.scheduleHint();
    });
  }

  disconnectedCallback() {
    this.cancelHint();
    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.reducedMotionQuery?.removeEventListener?.('change', this.handleMotionPreference);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.removeEventListener('pointerdown', this.handleUserTakeover);
    this.removeEventListener('touchstart', this.handleUserTakeover);
    this.removeEventListener('wheel', this.handleUserTakeover);
    this.removeEventListener('keydown', this.handleUserTakeover);
    this.removeEventListener('focusin', this.handleUserTakeover);
    this.initialized = false;
  }

  handleIntersection(entries) {
    this.isIntersecting = entries.some((entry) => entry.target === this && entry.isIntersecting);
    if (this.isIntersecting) this.scheduleHint();
    else this.cancelHint();
  }

  handleMotionPreference() {
    if (this.reducedMotionQuery?.matches) this.cancelHint();
    else this.scheduleHint();
  }

  handleResize() {
    this.scheduleHint();
  }

  handleVisibilityChange() {
    if (document.hidden) this.cancelHint();
    else this.scheduleHint();
  }

  handleUserTakeover() {
    this.userHasTakenOver = true;
    this.cancelHint();
  }

  canHint() {
    return (
      !this.userHasTakenOver &&
      !this.hasHinted &&
      this.initialized &&
      this.isConnected &&
      !this.reducedMotionQuery?.matches &&
      !document.hidden &&
      this.isIntersecting &&
      this.clientWidth > 0 &&
      this.scrollWidth > this.clientWidth + 1
    );
  }

  scheduleHint() {
    this.cancelHint();
    if (!this.canHint()) return;
    this.hintDelay = window.setTimeout(() => this.startHint(), 1500);
  }

  startHint() {
    this.hintDelay = null;
    if (!this.canHint()) return;

    this.hasHinted = true;
    this.hintStartPosition = this.scrollLeft;
    this.hintDistance = Math.min(48, this.scrollWidth - this.clientWidth - this.hintStartPosition);
    if (this.hintDistance < 1) return;

    this.hintStartTime = null;
    this.hintFrame = requestAnimationFrame(this.runHintFrame);
  }

  runHintFrame(timestamp) {
    if (this.userHasTakenOver || document.hidden) {
      this.cancelHint();
      return;
    }

    if (this.hintStartTime === null) this.hintStartTime = timestamp;
    const progress = Math.min((timestamp - this.hintStartTime) / 1300, 1);
    const easedProgress = 0.5 - Math.cos(Math.PI * progress) / 2;
    this.scrollLeft = this.hintStartPosition + this.hintDistance * easedProgress;

    if (progress < 1) this.hintFrame = requestAnimationFrame(this.runHintFrame);
    else this.hintFrame = null;
  }

  cancelHint() {
    if (this.setupFrame) cancelAnimationFrame(this.setupFrame);
    if (this.hintDelay) window.clearTimeout(this.hintDelay);
    if (this.hintFrame) cancelAnimationFrame(this.hintFrame);
    this.setupFrame = null;
    this.hintDelay = null;
    this.hintFrame = null;
  }
}

if (!customElements.get('active-facets-scroll')) {
  customElements.define('active-facets-scroll', ActiveFacetsScroll);
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
